import QRCode from 'qrcode'
import type { PicklistData } from './picklistPdf'

interface PreparedRow {
  index: number
  product: string
  qty: number
  picked: number
  size: 'Large' | 'Small' | 'Other'
}

// ─── Page geometry (4x6 label, points: 4in x 6in = 288 x 432) ───────────────
const PAGE_W = 288
const PAGE_H = 432
const MARGIN = 14
const RIGHT = PAGE_W - MARGIN // 274
const ROW_H = 19
const BOTTOM_LIMIT = 24

const X_BOX = MARGIN + 1
const X_PRODUCT = MARGIN + 16
const X_QTY = RIGHT - 26

// ─── helpers ─────────────────────────────────────────────────────────────────

function sizeOf(name: string): PreparedRow['size'] {
  const lower = name.toLowerCase()
  if (/\blarge\b/.test(lower)) return 'Large'
  if (/\bsmall\b/.test(lower)) return 'Small'
  return 'Other'
}

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

function pdfText(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0
    if (code > 255) {
      out += '?'
      continue
    }
    const c = String.fromCharCode(code)
    if (c === '\\') out += '\\\\'
    else if (c === '(') out += '\\('
    else if (c === ')') out += '\\)'
    else out += c
  }
  return out
}

function addressLines(raw: string): string[] {
  return raw
    .split(/\\n|\n/)
    .map(p => p.trim())
    .filter(Boolean)
}

/** Formats a `YYYY-MM-DD` (or similar) date string as e.g. "Dec 10, 2025". */
function formatOrderDate(raw: string): string {
  if (!raw) return ''
  const parsed = new Date(`${raw}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ─── content-stream operator builders ────────────────────────────────────────

function text(x: number, y: number, size: number, font: 'F1' | 'F2', value: string): string {
  return `BT /${font} ${num(size)} Tf 1 0 0 1 ${num(x)} ${num(y)} Tm (${pdfText(value)}) Tj ET\n`
}

function line(x1: number, y1: number, x2: number, y2: number, width = 1, gray = 0): string {
  return `${num(width)} w ${num(gray)} ${num(gray)} ${num(gray)} RG ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S\n`
}

function strokeRect(x: number, y: number, w: number, h: number, width = 1): string {
  return `${num(width)} w 0 0 0 RG ${num(x)} ${num(y)} ${num(w)} ${num(h)} re S\n`
}

/** Draws a check mark inside a box whose bottom-left corner is (x, y) with side length s. */
function checkMark(x: number, y: number, s: number): string {
  const p1x = x + s * 0.18
  const p1y = y + s * 0.5
  const p2x = x + s * 0.42
  const p2y = y + s * 0.24
  const p3x = x + s * 0.82
  const p3y = y + s * 0.78
  return `1.6 w 0 0 0 RG ${num(p1x)} ${num(p1y)} m ${num(p2x)} ${num(p2y)} l ${num(p3x)} ${num(p3y)} l S\n`
}

function fillRect(x: number, y: number, w: number, h: number): string {
  return `0 0 0 rg ${num(x)} ${num(y)} ${num(w)} ${num(h)} re f\n`
}

function fillRectGray(x: number, y: number, w: number, h: number, gray: number): string {
  return `${num(gray)} ${num(gray)} ${num(gray)} rg ${num(x)} ${num(y)} ${num(w)} ${num(h)} re f 0 0 0 rg\n`
}

// ─── PDF assembly ────────────────────────────────────────────────────────────

function buildPdf(pageContents: string[]): ArrayBuffer {
  const pageCount = pageContents.length
  const kids: string[] = []
  for (let i = 0; i < pageCount; i++) kids.push(`${5 + 2 * i} 0 R`)

  const objects: { num: number; body: string }[] = []
  objects.push({ num: 1, body: '<< /Type /Catalog /Pages 2 0 R >>' })
  objects.push({ num: 2, body: `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${pageCount} >>` })
  objects.push({ num: 3, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>' })
  objects.push({ num: 4, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>' })

  for (let i = 0; i < pageCount; i++) {
    const pageObj = 5 + 2 * i
    const contentObj = 6 + 2 * i
    objects.push({
      num: pageObj,
      body: `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObj} 0 R >>`,
    })
    const content = pageContents[i] ?? ''
    objects.push({
      num: contentObj,
      body: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    })
  }

  objects.sort((a, b) => a.num - b.num)

  let pdf = '%PDF-1.4\n'
  const offsets: number[] = []
  for (const obj of objects) {
    offsets[obj.num] = pdf.length
    pdf += `${obj.num} 0 obj\n${obj.body}\nendobj\n`
  }

  const xrefOffset = pdf.length
  const total = objects.length + 1
  pdf += `xref\n0 ${total}\r\n`
  pdf += '0000000000 65535 f\r\n'
  for (let n = 1; n < total; n++) {
    pdf += `${String(offsets[n] ?? 0).padStart(10, '0')} 00000 n\r\n`
  }
  pdf += `trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  const buffer = new ArrayBuffer(pdf.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff
  return buffer
}

// ─── label drawing ────────────────────────────────────────────────────────────

/** Draws a compact heading on continuation pages, returns the next top. */
function drawContinuationHeader(ops: string[], data: PicklistData, top: number): number {
  ops.push(text(MARGIN, top - 11, 12, 'F2', 'Pick List (cont.)'))
  ops.push(text(MARGIN, top - 26, 12, 'F2', data.order_id))
  const company = data.customer || '\u2014'
  const clippedCompany = company.length > 40 ? `${company.slice(0, 38)}...` : company
  ops.push(text(MARGIN, top - 40, 11, 'F2', clippedCompany))
  return top - 48
}

/** Draws the repeating column header at `top`, returns the next rowTop. */
function drawColumnHeader(ops: string[], top: number): number {
  ops.push(line(MARGIN, top, RIGHT, top, 1))
  const baseline = top - 10
  ops.push(text(X_PRODUCT, baseline, 9, 'F2', 'Product'))
  ops.push(text(X_QTY, baseline, 9, 'F2', 'Qty'))
  const ruleY = top - 14
  ops.push(line(MARGIN, ruleY, RIGHT, ruleY, 1))
  return ruleY - 1
}

/**
 * Builds a picklist PDF sized for a 4x6 label and opens it in a new tab.
 */
export async function generatePicklistLabelPdf(data: PicklistData): Promise<void> {
  const rows: PreparedRow[] = data.lines.map((lineItem, i) => {
    const product = lineItem.item_name?.trim() || '\u2014'
    return {
      index: i + 1,
      product,
      qty: Number(lineItem.quantity_ordered ?? 0),
      picked: Number(lineItem.quantity_picked ?? 0),
      size: sizeOf(product),
    }
  })

  const totalLarge = rows.filter(r => r.size === 'Large').reduce((s, r) => s + r.qty, 0)
  const totalSmall = rows.filter(r => r.size === 'Small').reduce((s, r) => s + r.qty, 0)
  const overall = rows.reduce((s, r) => s + r.qty, 0)

  const qr = QRCode.create(data.order_id, { errorCorrectionLevel: 'M' })
  const qrSize = qr.modules.size
  const qrData = qr.modules.data

  const pageContents: string[] = []
  let ops: string[] = ['0 0 0 rg\n0 0 0 RG\n']

  const flushPage = () => {
    pageContents.push(ops.join(''))
    ops = ['0 0 0 rg\n0 0 0 RG\n']
  }

  // ── Header ───────────────────────────────────────────────────────────────────
  const qrBox = 56
  const qrX = RIGHT - qrBox
  const dateLabel = formatOrderDate(data.order_date ?? '')
  let qrTop = PAGE_H - MARGIN
  if (dateLabel) {
    ops.push(text(qrX, qrTop - 7, 8, 'F2', dateLabel))
    qrTop -= 12
  }
  const moduleSize = qrBox / qrSize
  for (let r = 0; r < qrSize; r++) {
    for (let c = 0; c < qrSize; c++) {
      if (qrData[r * qrSize + c]) {
        const mx = qrX + c * moduleSize
        const my = qrTop - (r + 1) * moduleSize
        ops.push(fillRect(mx, my, moduleSize + 0.25, moduleSize + 0.25))
      }
    }
  }

  ops.push(text(MARGIN, PAGE_H - MARGIN - 9, 14, 'F2', 'Pick List'))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 27, 12, 'F2', data.order_id))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 41, 11, 'F2', data.customer || '\u2014'))

  let addrY = PAGE_H - MARGIN - 54
  const addr = addressLines(data.shipping_address)
  const addrMax = qrTop - qrBox - 4
  if (addr.length === 0) {
    ops.push(text(MARGIN, addrY, 8.5, 'F1', '\u2014'))
    addrY -= 11
  } else {
    for (const ln of addr) {
      const clipped = ln.length > 38 ? `${ln.slice(0, 36)}...` : ln
      ops.push(text(MARGIN, addrY, 8.5, 'F1', clipped))
      addrY -= 11
    }
  }

  // ── Table ───────────────────────────────────────────────────────────────────
  let rowTop = drawColumnHeader(ops, Math.min(addrY - 2, addrMax - 4))

  for (const row of rows) {
    if (rowTop - ROW_H < BOTTOM_LIMIT) {
      flushPage()
      const headerTop = drawContinuationHeader(ops, data, PAGE_H - MARGIN)
      rowTop = drawColumnHeader(ops, headerTop)
    }
    const baseline = rowTop - 13
    if (row.index % 2 === 0) {
      ops.push(fillRectGray(MARGIN, rowTop - ROW_H, RIGHT - MARGIN, ROW_H, 0.93))
    }
    ops.push(strokeRect(X_BOX, baseline - 1, 11, 11, 1))
    const fullyPicked = row.qty > 0 && row.picked >= row.qty
    const partiallyPicked = row.picked > 0 && row.picked < row.qty
    if (fullyPicked) ops.push(checkMark(X_BOX, baseline - 1, 11))
    const maxChars = 30
    const product = row.product.length > maxChars ? `${row.product.slice(0, maxChars - 2)}...` : row.product
    ops.push(text(X_PRODUCT, baseline, 9.5, 'F1', product))
    const qtyLabel = partiallyPicked ? `${row.picked}/${row.qty}` : String(row.qty)
    ops.push(text(X_QTY, baseline, 9.5, partiallyPicked ? 'F2' : 'F1', qtyLabel))
    rowTop -= ROW_H
    ops.push(line(MARGIN, rowTop, RIGHT, rowTop, 0.4, 0.82))
  }

  // ── Totals ───────────────────────────────────────────────────────────────────
  if (rowTop - 60 < BOTTOM_LIMIT) {
    flushPage()
    rowTop = drawContinuationHeader(ops, data, PAGE_H - MARGIN)
  }
  let ty = rowTop - 14
  const labelX = RIGHT - 130
  const valueX = RIGHT - 22
  ops.push(line(labelX, ty + 7, RIGHT, ty + 7, 1))
  ops.push(text(labelX, ty - 4, 10, 'F1', 'Total Large'))
  ops.push(text(valueX, ty - 4, 10, 'F1', String(totalLarge)))
  ty -= 15
  ops.push(text(labelX, ty - 4, 10, 'F1', 'Total Small'))
  ops.push(text(valueX, ty - 4, 10, 'F1', String(totalSmall)))
  ty -= 16
  ops.push(line(labelX, ty + 6, RIGHT, ty + 6, 1))
  ops.push(text(labelX, ty - 5, 11, 'F2', 'Overall Total'))
  ops.push(text(valueX, ty - 5, 11, 'F2', String(overall)))

  flushPage()

  const bytes = buildPdf(pageContents)
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
